import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Loader from '../Components/Loader';
import SuccessCard from '../Components/SuccessCard';
import ErrorCard from '../Components/ErrorCard';
import useUpdateCISBohol from '../Hooks/CisHooks/useUpdateCISBohol';
import useCISBoholLabels from '../Hooks/CisHooks/useCISBoholLabels';
import useIAList from '../Hooks/CisHooks/useIAListBohol';
import MunicipalitySelector from '../Components/Municipality';
import MailingAddressSelector from '../Components/MailingAddressSelector';
import adminBg from '../Assets/bg/adminbg.jpg';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorCard
          errorMessage={`Rendering error: ${this.state.error?.message || 'Unknown error'}`}
          onClose={this.props.onClose}
        />
      );
    }
    return this.props.children;
  }
}

const CISboholEdit = ({ selectedItem, onClose, darkMode }) => {
  const { updateCIS, isLoading, error, success } = useUpdateCISBohol();
  const { iaList, loading: iaListLoading, error: iaListError } = useIAList();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const { labels, loading: labelsLoading, error: labelsError } = useCISBoholLabels();


  // Initial form data
  const initialFormData = {
    years: '',
    label: '',
    ia_name: '',
    num: '',
    cis_name: '',
    districtNum: '',
    municipality: '',
    mailing_address: '',
    president: '',
    contactNum: '',
    email_address: '',
    dateOrganized: '',
    sec_RegistrationNum: '',
    sec_DateApproved: '',
    tin: '',
    water_permitNum: '',
    water_dateApproved: '',
    membership_fb: '',
    membership_actual: '',
    sectorNum: '',
    serviceArea: '',
    fusa: '',
    operational: '',
    date_moaSigned: '',
    f_source: '',
    projCost: '',
    date_turnOver: '',
    ia_existContract: '',
    imt_dateSigned: '',
    imt_undercontract: '',
    canalLength_lined: '',
    canalLength_unlined: '',
    total_CanalLength: '',
    fundSource: '',
    category: '',
    devolutionPhases: '',
    idoAssigned: '',
    remarks: '',
    custom_is_name: '',
    custom_ia_name: '',
    custom_label: '',
  };


  // Convert date to YYYY-MM-DD format
  const convertDateFormat = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

  // Initialize form state and original data with selectedItem
  const [formData, setFormData] = useState({
    years: selectedItem?.years?.toString() || '',
    label: selectedItem?.label || '',
    ia_name: selectedItem?.ia_name || '',
    num: selectedItem?.num || '',
    cis_name: selectedItem?.cis_name || '',
    districtNum: selectedItem?.districtNum || '',
    municipality: selectedItem?.municipality ? toTitleCase(selectedItem.municipality) : '',
    mailing_address: selectedItem?.mailing_address || selectedItem?.mailingAddress || '',
    president: selectedItem?.president || '',
    contactNum: selectedItem?.contactNum || '',
    email_address: selectedItem?.email_address || '',
    dateOrganized: convertDateFormat(selectedItem?.dateOrganized) || '',
    sec_RegistrationNum: selectedItem?.sec_RegistrationNum || '',
    sec_DateApproved: convertDateFormat(selectedItem?.sec_DateApproved) || '',
    tin: selectedItem?.tin || '',
    water_permitNum: selectedItem?.water_permitNum || '',
    water_dateApproved: convertDateFormat(selectedItem?.water_dateApproved) || '',
    membership_fb: selectedItem?.membership_fb || '',
    membership_actual: selectedItem?.membership_actual || '',
    sectorNum: selectedItem?.sectorNum || '',
    serviceArea: selectedItem?.serviceArea || '',
    fusa: selectedItem?.fusa || '',
    operational: selectedItem?.operational || '',
    date_moaSigned: convertDateFormat(selectedItem?.date_moaSigned) || '',
    f_source: selectedItem?.f_source || '',
    projCost: selectedItem?.projCost || '',
    date_turnOver: convertDateFormat(selectedItem?.date_turnOver) || '',
    ia_existContract: selectedItem?.ia_existContract || '',
    imt_dateSigned: convertDateFormat(selectedItem?.imt_dateSigned) || '',
    imt_undercontract: selectedItem?.imt_undercontract || '',
    canalLength_lined: selectedItem?.canalLength_lined || '',
    canalLength_unlined: selectedItem?.canalLength_unlined || '',
    total_CanalLength: selectedItem?.total_CanalLength || '',
    fundSource: selectedItem?.fundSource || '',
    category: selectedItem?.category || '',
    devolutionPhases: selectedItem?.devolutionPhases || '',
    idoAssigned: selectedItem?.idoAssigned || '',
    remarks: selectedItem?.remarks || '',
    custom_is_name: '',
    custom_ia_name: '',
    custom_label: '',
  });

  const [originalData, setOriginalData] = useState(formData);

   // Update formData and originalData when selectedItem changes
  useEffect(() => {
    if (selectedItem) {
      const updatedData = {
        years: selectedItem?.years?.toString() || '',
        label: selectedItem?.label || '',
        ia_name: selectedItem?.ia_name || '',
        num: selectedItem?.num || '',
        cis_name: selectedItem?.cis_name || '',
        districtNum: selectedItem?.districtNum || '',
        municipality: selectedItem?.municipality ? toTitleCase(selectedItem.municipality) : '',
        mailing_address: selectedItem?.mailing_address || selectedItem?.mailingAddress || '',
        president: selectedItem?.president || '',
        contactNum: selectedItem?.contactNum || '',
        email_address: selectedItem?.email_address || '',
        dateOrganized: convertDateFormat(selectedItem?.dateOrganized) || '',
        sec_RegistrationNum: selectedItem?.sec_RegistrationNum || '',
        sec_DateApproved: convertDateFormat(selectedItem?.sec_DateApproved) || '',
        tin: selectedItem?.tin || '',
        water_permitNum: selectedItem?.water_permitNum || '',
        water_dateApproved: convertDateFormat(selectedItem?.water_dateApproved) || '',
        membership_fb: selectedItem?.membership_fb || '',
        membership_actual: selectedItem?.membership_actual || '',
        sectorNum: selectedItem?.sectorNum || '',
        serviceArea: selectedItem?.serviceArea || '',
        fusa: selectedItem?.fusa || '',
        operational: selectedItem?.operational || '',
        date_moaSigned: convertDateFormat(selectedItem?.date_moaSigned) || '',
        f_source: selectedItem?.f_source || '',
        projCost: selectedItem?.projCost || '',
        date_turnOver: convertDateFormat(selectedItem?.date_turnOver) || '',
        ia_existContract: selectedItem?.ia_existContract || '',
        imt_dateSigned: convertDateFormat(selectedItem?.imt_dateSigned) || '',
        imt_undercontract: selectedItem?.imt_undercontract || '',
        canalLength_lined: selectedItem?.canalLength_lined || '',
        canalLength_unlined: selectedItem?.canalLength_unlined || '',
        total_CanalLength: selectedItem?.total_CanalLength || '',
        fundSource: selectedItem?.fundSource || '',
        category: selectedItem?.category || '',
        devolutionPhases: selectedItem?.devolutionPhases || '',
        idoAssigned: selectedItem?.idoAssigned || '',
        remarks: selectedItem?.remarks || '',
        custom_is_name: '',
        custom_ia_name: '',
        custom_label: '',
      };
      setFormData(updatedData);
      setOriginalData(updatedData);
    }
  }, [selectedItem]);

  const formSections = [
    {
      title: null,
      fields: ['years', 'label'],
    },
    {
      title: null,
      fields: [
        'ia_name',
        'cis_name',
        'num',
        'municipality',
        'districtNum',
        'mailing_address',
        'president',
        'contactNum',
        'email_address',
        'dateOrganized',
      ],
    },
    {
      title: 'SEC/CDA/DOLE Reg.',
      fields: ['sec_RegistrationNum', 'sec_DateApproved'],
    },
    {
      title: 'Water Permit',
      fields: ['water_permitNum', 'water_dateApproved'],
    },
    {
      title: null,
      fields: ['tin'],
    },
    {
      title: 'Membership',
      fields: ['membership_fb', 'membership_actual'],
    },
    {
      title: null,
      fields: ['sectorNum', 'serviceArea', 'fusa', 'operational'],
    },
    {
      title: 'MOA',
      fields: ['date_moaSigned'],
    },
    {
      title: 'Project Details',
      fields: ['f_source', 'projCost', 'date_turnOver'],
    },
    {
      title: 'Contract Details',
      fields: ['ia_existContract', 'imt_dateSigned', 'imt_undercontract'],
    },
    {
      title: 'Canal Details',
      fields: ['canalLength_lined', 'canalLength_unlined', 'total_CanalLength'],
    },
    {
      title: 'Other Details',
      fields: ['fundSource', 'category', 'devolutionPhases', 'idoAssigned', 'remarks'],
    },
  ];

  const fieldLabelMap = {
    years: 'Year',
    label: 'Label',
    ia_name: "Name of Irrigators' Association",
    num: 'Number',
    cis_name: 'CIS Name',
    municipality: 'Municipality',
    districtNum: 'District No.',
    mailing_address: 'Mailing Address',
    president: 'Name of IA President',
    contactNum: 'Contact Number',
    email_address: 'Email Address',
    dateOrganized: 'Date Organized',
    sec_RegistrationNum: 'SEC Registration Number',
    sec_DateApproved: 'SEC Date Approved',
    tin: 'TIN',
    water_permitNum: 'Water Permit Number',
    water_dateApproved: 'Water Permit Date Approved',
    membership_fb: 'Membership (Farmer Beneficiaries)',
    membership_actual: 'Membership (Actual)',
    sectorNum: 'Sector Number',
    serviceArea: 'Service Area',
    fusa: 'Fusa',
    operational: 'Operational Area',
    date_moaSigned: 'Date MOA Signed',
    f_source: 'Funding Source',
    projCost: 'Project Cost',
    date_turnOver: 'Date Turned Over',
    ia_existContract: 'Existing Contract',
    imt_dateSigned: 'IMT Date Signed',
    imt_undercontract: 'IMT Under Contract',
    canalLength_lined: 'Canal Length (Lined)',
    canalLength_unlined: 'Canal Length (Unlined)',
    total_CanalLength: 'Total Canal Length',
    fundSource: 'Fund Source',
    category: 'Category',
    devolutionPhases: 'Devolution Phases',
    idoAssigned: 'IDO Assigned',
    remarks: 'Remarks',
    custom_is_name: 'Custom CIS Name',
    custom_ia_name: 'Custom IA Name',
    custom_label: 'Custom Label',
  };

  // Generate year options (2000 to current year)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => (currentYear - i).toString());

  // Log for debugging
  useEffect(() => {
    console.log('CISboholEdit received selectedItem:', selectedItem);
    console.log('selectedItem.municipality:', selectedItem?.municipality);
    console.log('formData:', formData);
    console.log('Problematic fields:', {
      years: formData.years,
      municipality: formData.municipality,
      mailing_address: formData.mailing_address,
    });
    console.log('Selected Municipality:', formData.municipality);
    console.log('Selected Mailing Address:', formData.mailing_address);
    console.log('iaList:', iaList);
    if (!selectedItem || !selectedItem.id) {
      console.warn('Invalid selectedItem');
      setShowError(true);
    }
  }, [selectedItem, iaList, formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

const validateForm = () => {
  const errors = {};

  // Only validate custom fields if their respective dropdowns are set to 'Others ...'
  if (formData.cis_name === 'Others ...' && !formData.custom_is_name.trim()) {
    errors.custom_is_name = true;
  }
  if (formData.ia_name === 'Others ...' && !formData.custom_ia_name.trim()) {
    errors.custom_ia_name = true;
  }
  if (formData.label === 'Others ...' && !formData.custom_label.trim()) {
    errors.custom_label = true;
  }

  setValidationErrors(errors);
  return Object.keys(errors).length === 0;
};

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) {
    setShowError(true);
    console.log('Validation errors:', validationErrors);
    return;
  }

  const submissionData = {
    ...formData,
    cis_name: formData.cis_name === 'Others ...' ? formData.custom_is_name.toUpperCase() : formData.cis_name,
    ia_name: formData.ia_name === 'Others ...' ? formData.custom_ia_name.toUpperCase() : formData.ia_name,
    label: formData.label === 'Others ...' ? formData.custom_label.toUpperCase() : formData.label,
  };
  try {
    await updateCIS(selectedItem.id, submissionData);
    setShowSuccess(true);
    setShowError(false);
    setTimeout(onClose, 1000);
  } catch (err) {
    setShowError(true);
    setShowSuccess(false);
    console.error('Update error:', err);
  }
};

// Update the error message display logic
{showError && Object.keys(validationErrors).length > 0 && (
  <ErrorMessage>Please fill out the required custom fields correctly.</ErrorMessage>
)}


  const handleClose = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    setShowSuccess(false);
    setShowError(false);
    onClose();
  };

  if (!selectedItem || !selectedItem.id) {
    return (
      <RightPanel darkMode={darkMode} show={true}>
        <FormContainer darkMode={darkMode}>
          <Title darkMode={darkMode}>
            <span>Edit CIS Record</span>
            <CloseButton onClick={handleClose} darkMode={darkMode}>
              X
            </CloseButton>
          </Title>
          <ErrorCard errorMessage="No valid record selected." onClose={handleClose} />
        </FormContainer>
      </RightPanel>
    );
  }

  return (
    <RightPanel darkMode={darkMode} show={true}>
      <FormContainer darkMode={darkMode}>
        <Title darkMode={darkMode}>
          <span>Edit CIS Record</span>
          <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
        </Title>
        {(isLoading || iaListLoading) && (
          <LoaderOverlay>
            <Loader />
          </LoaderOverlay>
        )}
        {(error || iaListError) && (
          <ErrorMessage>{error || iaListError}</ErrorMessage>
        )}
        {showSuccess && (
          <SuccessCard
            message="CIS data updated successfully!"
            onClose={() => setShowSuccess(false)}
          />
        )}
        {showError && (
          <ErrorMessage>Please fill out all fields correctly.</ErrorMessage>
        )}
        <ErrorBoundary onClose={handleClose}>
          <StyledForm onSubmit={handleSubmit}>
            {formSections.map((section, index) => (
              <CardSection darkMode={darkMode} key={index}>
                {section.title && (
                  <SectionHeader darkMode={darkMode}>{section.title}</SectionHeader>
                )}
                {section.fields.map((key) => (
                  <FieldGroup key={key} darkMode={darkMode} hasError={validationErrors[key]}>
                    <Label htmlFor={key} hasError={validationErrors[key]}>
                      {fieldLabelMap[key]}
                      <RequiredAsterisk>*</RequiredAsterisk>
                    </Label>
                    {key === 'years' ? (
                      (() => {
                        const currentYear = new Date().getFullYear();
                        const yearOptions = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => (currentYear - i).toString());

                        const options = yearOptions.includes(formData.years)
                          ? yearOptions
                          : [formData.years, ...yearOptions];

                        return (
                          <Select
                            id={key}
                            name={key}
                            value={formData[key]}
                            onChange={handleChange}
                            aria-label={fieldLabelMap[key]}
                            darkMode={darkMode}
                            hasError={validationErrors[key]}
                          >
                            <option value="">Select Year</option>
                            {options.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </Select>
                        );
                      })()
                    ) : key === 'label' ? (
                      <>
                        <Select
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          aria-label={fieldLabelMap[key]}
                          hasError={validationErrors[key]}
                        >
                          <option value="" disabled>Select Label</option>
                          {labels.map((label, idx) => (
                            <option key={idx} value={label.label}>
                              {label.label}
                            </option>
                          ))}
                          <option value="Others ...">Add new Label</option>
                        </Select>
                        {formData.label === 'Others ...' && (
                          <Input
                            type="text"
                            id="custom_label"
                            name="custom_label"
                            value={formData.custom_label || ''}
                            onChange={handleChange}
                            placeholder="Enter Custom Label"
                            aria-label="Custom Label"
                            hasError={validationErrors.custom_label}
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </>
                    ) : key === 'ia_name' ? (
                      <>
                        <Select
                          id={key}
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          aria-label={fieldLabelMap[key]}
                          darkMode={darkMode}
                          hasError={validationErrors[key]}
                        >
                          <option value="">Select IA Name</option>
                          {iaList?.length > 0 ? (
                            iaList.map((ia) => (
                              <option key={ia.ia_name} value={ia.ia_name}>
                                {ia.ia_name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No IA names available
                            </option>
                          )}
                          <option value="Others ...">Add new IA</option>
                        </Select>
                        {formData.ia_name === 'Others ...' && (
                          <Input
                            type="text"
                            id="custom_ia_name"
                            name="custom_ia_name"
                            value={formData.custom_ia_name}
                            onChange={handleChange}
                            placeholder="Enter Custom IA Name"
                            aria-label={fieldLabelMap.custom_ia_name}
                            darkMode={darkMode}
                            hasError={validationErrors.custom_ia_name}
                            style={{ marginTop: '8px' }}
                          />
                        )}
                      </>
                    ) : key === 'municipality' ? (
                      <MunicipalitySelector
                        formData={formData}
                        setFormData={setFormData}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : key === 'mailing_address' ? (
                      <MailingAddressSelector
                        formData={formData}
                        setFormData={setFormData}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : key === 'dateOrganized' ||
                      key === 'sec_DateApproved' ||
                      key === 'water_dateApproved' ||
                      key === 'date_moaSigned' ||
                      key === 'date_turnOver' ||
                      key === 'imt_dateSigned' ? (
                      <Input
                        type="date"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        aria-label={fieldLabelMap[key]}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : key === 'num' ||
                      key === 'districtNum' ||
                      key === 'sectorNum' ||
                      key === 'membership_fb' ||
                      key === 'membership_actual' ||
                      key === 'serviceArea' ||
                      key === 'fusa' ||
                      key === 'operational' ||
                      key === 'projCost' ||
                      key === 'canalLength_lined' ||
                      key === 'canalLength_unlined' ||
                      key === 'total_CanalLength' ? (
                      <Input
                        type="number"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        placeholder={fieldLabelMap[key]}
                        aria-label={fieldLabelMap[key]}
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    ) : (
                      <Input
                        type="text"
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        placeholder={
                          key === 'contactNum'
                            ? 'Enter Contact Number (7-15 digits)'
                            : key === 'tin'
                            ? 'Enter TIN (alphanumeric, up to 20 chars)'
                            : fieldLabelMap[key]
                        }
                        aria-label={fieldLabelMap[key]}
                        maxLength={
                          key === 'contactNum' ? '15' :
                          key === 'tin' ? '20' :
                          key === 'email_address' ? '100' :
                          '200'
                        }
                        darkMode={darkMode}
                        hasError={validationErrors[key]}
                      />
                    )}
                  </FieldGroup>
                ))}
              </CardSection>
            ))}
            <FormButtonContainer>
              <SubmitButton type="submit" darkMode={darkMode} disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Information'}
              </SubmitButton>
            </FormButtonContainer>
          </StyledForm>
        </ErrorBoundary>
      </FormContainer>
    </RightPanel>
  );
};

export default CISboholEdit;

// Styled Components
const RightPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${({ show }) => (show ? '0' : '-45%')};
  width: 45%;
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  z-index: 5;
  padding: 20px;
  transition: right 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5px;
  flex-grow: 1;
  overflow-y: auto;
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-radius: 8px;
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 5px;
  margin: 0 0 10px 0;
  font-size: 22px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    flex: 1;
    text-align: center;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  cursor: pointer;
  padding: 5px 10px;
  transition: color 0.2s ease;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    color: ${({ darkMode }) => (darkMode ? '#ff5555' : '#cc0000')};
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
`;

const CardSection = styled.div`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: ${({ darkMode }) => (darkMode ? '0 3px 8px rgba(255, 255, 255, 0.2)' : '0 3px 10px rgba(0, 0, 0, 0.1)')};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  grid-column: span 2;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#ccc' : '#ddd')};
  padding-bottom: 5px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 5px;
  color: ${({ darkMode, hasError }) => (hasError ? '#ff0000' : darkMode ? '#f2f2f2' : '#333')};
`;

const RequiredAsterisk = styled.span`
  color: #ff0000;
  margin-left: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: ${({ darkMode }) => (darkMode ? '#333' : '#ffffff')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? '#444' : '#f1f1f1')};
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: ${({ darkMode }) => (darkMode ? '#333' : '#ffffff')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? '#444' : '#f1f1f1')};
  }

  option {
    color: #333;
    background-color: #fff;
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin: 10px 0;
  padding-bottom: 60px;
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ darkMode }) => (darkMode ? '#4caf50' : '#18591d')};
  color: #fff;
  border: 1px solid ${({ darkMode }) => (darkMode ? '#388e3c' : '#144d17')};
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ darkMode }) => (darkMode ? '#388e3c' : '#218838')};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
`;